import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, ChevronRight, ArrowDown, ArrowUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/content/EmptyState";
import { useAdminUsers, type AdminUserFilters } from "@/hooks/useAdmin";
import { formatDate, formatRelative } from "@/lib/format";
import { isProfileComplete } from "@/lib/profile";
import { signupSourceLabel } from "@/lib/signupSource";
import { ProfileBadge, RoleBadge, StatusBadge } from "@/pages/admin/adminBadges";
import { USER_ROLES, USER_STATUSES } from "@/pages/admin/adminConstants";
import type { User } from "@/lib/api-types";

const selectClass =
  "h-10 rounded-lg border border-border bg-surface dark:bg-surface-2 px-3 text-sm text-text " +
  "transition-[border-color,box-shadow] duration-150 hover:border-border-strong " +
  "focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/40";

type Sort = NonNullable<AdminUserFilters["sort"]>;
type ProfileFilter = "" | "complete" | "incomplete";

export function AdminUsersPage() {
  const [roleId, setRoleId] = useState<User["role_id"] | "">("");
  const [status, setStatus] = useState<User["user_status"] | "">("");
  const [profile, setProfile] = useState<ProfileFilter>("");
  // Sort lives in the URL so a refresh / shared link keeps the order.
  const [params, setParams] = useSearchParams();
  const sort: Sort = params.get("sort") === "created_asc" ? "created_asc" : "created_desc";
  const toggleSort = () =>
    setParams(
      (p) => {
        p.set("sort", sort === "created_desc" ? "created_asc" : "created_desc");
        return p;
      },
      { replace: true },
    );

  const filters: AdminUserFilters = { sort };
  if (roleId) filters.role_id = roleId;
  if (status) filters.user_status = status;

  const { data: rows, isLoading, isError } = useAdminUsers(filters);
  // Completeness is derived from fields already on each row, so filter here.
  const data = rows?.filter((u) => {
    if (!profile) return true;
    if (u.role_id !== "STUDENT") return false;
    return profile === "complete" ? isProfileComplete(u) : !isProfileComplete(u);
  });

  const SortIcon = sort === "created_desc" ? ArrowDown : ArrowUp;

  return (
    <div>
      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
          Role
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value as User["role_id"] | "")}
            className={selectClass}
          >
            <option value="">All roles</option>
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as User["user_status"] | "")}
            className={selectClass}
          >
            <option value="">All statuses</option>
            {USER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
          Onboarding
          <select
            value={profile}
            onChange={(e) => setProfile(e.target.value as ProfileFilter)}
            className={selectClass}
          >
            <option value="">Any</option>
            <option value="incomplete">Incomplete (students)</option>
            <option value="complete">Complete (students)</option>
          </select>
        </label>
        <span className="ml-auto self-end pb-2.5 text-sm text-text-muted">
          {data ? `${data.length} user${data.length === 1 ? "" : "s"}` : null}
        </span>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={Users}
          title="Couldn't load users"
          body="The user list failed to load. Try adjusting filters or refreshing."
        />
      ) : !data || data.length === 0 ? (
        <EmptyState icon={Users} title="No users match these filters" />
      ) : (
        <Card className="overflow-hidden p-0">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-text-subtle">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Profile</th>
                  <th className="px-4 py-3">Source</th>
                  <th
                    className="px-4 py-3"
                    aria-sort={sort === "created_desc" ? "descending" : "ascending"}
                  >
                    <button
                      type="button"
                      onClick={toggleSort}
                      className="inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-text"
                      title={sort === "created_desc" ? "Newest first" : "Oldest first"}
                    >
                      Joined
                      <SortIcon className="h-3.5 w-3.5" />
                    </button>
                  </th>
                  <th className="px-4 py-3">Last seen</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr
                    key={u.user_id}
                    className="group border-b border-border last:border-0 transition-colors hover:bg-surface-2"
                  >
                    <td className="px-4 py-3">
                      <Link to={`/admin/users/${u.user_id}`} className="block">
                        <span className="font-medium text-text">
                          {u.user_name || "(no name)"}
                        </span>
                        <span className="block text-xs text-text-muted">{u.email}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role_id} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={u.user_status} />
                    </td>
                    <td className="px-4 py-3">
                      <ProfileBadge user={u} />
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {u.signup_source ? signupSourceLabel(u.signup_source) : "--"}
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {u.created_at ? formatDate(u.created_at) : "--"}
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {u.last_login_at ? formatRelative(u.last_login_at) : "--"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/users/${u.user_id}`}
                        className="inline-flex items-center text-text-subtle transition-colors group-hover:text-brand"
                        aria-label={`View ${u.email}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex items-center justify-end border-b border-border px-4 py-2 md:hidden">
            <button
              type="button"
              onClick={toggleSort}
              className="inline-flex items-center gap-1 text-xs font-medium text-text-muted"
            >
              {sort === "created_desc" ? "Newest first" : "Oldest first"}
              <SortIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="divide-y divide-border md:hidden">
            {data.map((u) => (
              <li key={u.user_id}>
                <Link
                  to={`/admin/users/${u.user_id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-surface-2"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text">
                      {u.user_name || "(no name)"}
                    </p>
                    <p className="truncate text-xs text-text-muted">
                      {u.email}
                      {u.created_at ? ` · joined ${formatDate(u.created_at)}` : ""}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <RoleBadge role={u.role_id} />
                      <StatusBadge status={u.user_status} />
                      {u.role_id === "STUDENT" ? <ProfileBadge user={u} /> : null}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-text-subtle" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
